import React, { useEffect, useState, Fragment } from 'react';
import axios from 'axios';
import CustomLoader from '../../components/CustomLoader';
import './productAdmin.scss';

const initialProductState = {
    name: '',
    image: '',
    link: '',
    shortDescription: '',
    longDescription: '',
    isActive: true
};

const ProductFormModal = ({ title, product, onChange, onSubmit, onClose }) => (
    <div className="dpa-modal__backdrop" onClick={onClose}>
        <div className="dpa-modal" onClick={e => e.stopPropagation()}>
            <div className="dpa-modal__header">
                <h3 className="dpa-modal__title">{title}</h3>
                <button className="dpa-modal__close" onClick={onClose}>✕</button>
            </div>
            <div className="dpa-modal__body">
                {[
                    { field: 'name',             label: 'Product Name',       type: 'text' },
                    { field: 'image',            label: 'Image URL',          type: 'url'  },
                    { field: 'link',             label: 'Product Link (URL)', type: 'url'  },
                    { field: 'shortDescription', label: 'Short Description',  type: 'textarea' },
                    { field: 'longDescription',  label: 'Long Description',   type: 'textarea' },
                ].map(({ field, label, type }) => (
                    <div className="dpa-modal__field" key={field}>
                        <label className="dpa-modal__label">{label}</label>
                        {type === 'textarea' ? (
                            <textarea
                                className="dpa-modal__input dpa-modal__input--ta"
                                rows={3}
                                value={product[field] || ''}
                                onChange={e => onChange(field, e.target.value)}
                            />
                        ) : (
                            <input
                                className="dpa-modal__input"
                                type={type}
                                value={product[field] || ''}
                                onChange={e => onChange(field, e.target.value)}
                            />
                        )}
                    </div>
                ))}
                <div className="dpa-modal__field dpa-modal__field--check">
                    <label className="dpa-modal__check-label">
                        <input
                            type="checkbox"
                            checked={!!product.isActive}
                            onChange={e => onChange('isActive', e.target.checked)}
                        />
                        <span>{product.isActive ? 'Active' : 'Inactive'}</span>
                    </label>
                </div>
            </div>
            <div className="dpa-modal__footer">
                <button className="dpa-modal__btn dpa-modal__btn--cancel" onClick={onClose}>Cancel</button>
                <button className="dpa-modal__btn dpa-modal__btn--save" onClick={onSubmit}>Save</button>
            </div>
        </div>
    </div>
);

const AdminProductPage = () => {
    const [products, setProducts]     = useState([]);
    const [loading, setLoading]       = useState(false);
    const [editProduct, setEditProduct] = useState(null);
    const [newProduct, setNewProduct]   = useState(initialProductState);
    const [showAdd, setShowAdd]         = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const res = await axios.get(process.env.REACT_APP_API_BASE_URL + '/products');
            setProducts(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchProducts(); }, []);

    const handleAddProduct = async () => {
        try {
            setLoading(true);
            await axios.post(process.env.REACT_APP_API_BASE_URL + '/products/update', newProduct);
            setNewProduct(initialProductState);
            setShowAdd(false);
            fetchProducts();
        } catch (e) { console.error(e); setLoading(false); }
    };

    const handleSaveEdit = async () => {
        try {
            setLoading(true);
            await axios.put(process.env.REACT_APP_API_BASE_URL + '/products/update', editProduct);
            setEditProduct(null);
            fetchProducts();
        } catch (e) { console.error(e); setLoading(false); }
    };

    const handleDelete = async (_id) => {
        try {
            setLoading(true);
            await axios.delete(process.env.REACT_APP_API_BASE_URL + '/products/delete', { data: { _id } });
            setConfirmDelete(null);
            fetchProducts();
        } catch (e) { console.error(e); setLoading(false); }
    };

    return (
        <Fragment>
            <CustomLoader show={loading} />

            <div className="dpa">
                {/* Header */}
                <div className="dpa__header">
                    <h1 className="dpa__title">Products</h1>
                    <button className="dpa__add-btn" onClick={() => setShowAdd(true)}>
                        <span>+</span> Add Product
                    </button>
                </div>

                {/* Card Grid */}
                {products.length === 0 && !loading ? (
                    <div className="dpa__empty">No products found.</div>
                ) : (
                    <div className="dpa__grid">
                        {products.map(p => (
                            <div className="dpa__card" key={p._id}>
                                <div className="dpa__card-img-wrap">
                                    {p.image ? (
                                        <img src={p.image} alt={p.name} className="dpa__card-img" onError={e => { e.target.style.display='none'; }} />
                                    ) : (
                                        <div className="dpa__card-img-placeholder">🧪</div>
                                    )}
                                    <span className={`dpa__badge${p.isActive ? ' dpa__badge--active' : ' dpa__badge--inactive'}`}>
                                        {p.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className="dpa__card-body">
                                    <h4 className="dpa__card-name">{p.name}</h4>
                                    {p.shortDescription && (
                                        <p className="dpa__card-short">{p.shortDescription}</p>
                                    )}
                                    {p.longDescription && (
                                        <p className="dpa__card-long">{p.longDescription}</p>
                                    )}
                                    {p.link && (
                                        <a href={p.link} className="dpa__card-link" target="_blank" rel="noreferrer">
                                            View Product ↗
                                        </a>
                                    )}
                                </div>
                                <div className="dpa__card-actions">
                                    <button className="dpa__action-btn dpa__action-btn--edit" onClick={() => setEditProduct({ ...p })}>
                                        ✏️ Edit
                                    </button>
                                    <button className="dpa__action-btn dpa__action-btn--delete" onClick={() => setConfirmDelete(p)}>
                                        🗑️ Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add Modal */}
            {showAdd && (
                <ProductFormModal
                    title="Add New Product"
                    product={newProduct}
                    onChange={(f, v) => setNewProduct(prev => ({ ...prev, [f]: v }))}
                    onSubmit={handleAddProduct}
                    onClose={() => setShowAdd(false)}
                />
            )}

            {/* Edit Modal */}
            {editProduct && (
                <ProductFormModal
                    title="Edit Product"
                    product={editProduct}
                    onChange={(f, v) => setEditProduct(prev => ({ ...prev, [f]: v }))}
                    onSubmit={handleSaveEdit}
                    onClose={() => setEditProduct(null)}
                />
            )}

            {/* Delete Confirm */}
            {confirmDelete && (
                <div className="dpa-modal__backdrop" onClick={() => setConfirmDelete(null)}>
                    <div className="dpa-modal dpa-modal--sm" onClick={e => e.stopPropagation()}>
                        <div className="dpa-modal__header">
                            <h3 className="dpa-modal__title">Delete Product</h3>
                            <button className="dpa-modal__close" onClick={() => setConfirmDelete(null)}>✕</button>
                        </div>
                        <div className="dpa-modal__body">
                            <p>Are you sure you want to delete <strong>{confirmDelete.name}</strong>? This cannot be undone.</p>
                        </div>
                        <div className="dpa-modal__footer">
                            <button className="dpa-modal__btn dpa-modal__btn--cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
                            <button className="dpa-modal__btn dpa-modal__btn--delete" onClick={() => handleDelete(confirmDelete._id)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default AdminProductPage;
